import React from "react";
import OnboardingCard from "../../components/OnboardingComponents/OnboardingCard";
import OnboardingButton from "../../components/OnboardingComponents/OnboardingButton";
import OnboardingSelect from "../../components/OnboardingComponents/OnboardingSelect";

interface PaginationControlProps {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  isFetching: boolean;
  pageInput: string;
  setPageInput: React.Dispatch<React.SetStateAction<string>>;
  pageSize: number;
  setPageSize: (size: number) => void;
}

export const PaginationControl = ({
  page,
  setPage,
  totalPages,
  isFetching,
  pageInput,
  setPageInput,
  pageSize,
  setPageSize,
}: PaginationControlProps) => {
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="mt-10 mb-6">
      <OnboardingCard className="p-4 md:px-8 md:py-6 flex flex-col xl:flex-row xl:items-center justify-between gap-6">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full xl:w-auto">
          <div className="flex w-full sm:w-auto gap-3 order-2 sm:order-1">
            <div className="flex-1 sm:w-[120px]">
              <OnboardingButton
                title="PREVIOUS"
                variant="outline"
                className="!py-3"
                onClick={() => setPage((p: number) => Math.max(1, p - 1))}
                disabled={page <= 1 || isFetching}
              />
            </div>
            <div className="flex-1 sm:hidden">
              <OnboardingButton
                title="NEXT"
                variant="outline"
                className="!py-3"
                onClick={() => setPage((p: number) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isFetching}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-1 order-1 sm:order-2 w-full sm:w-auto">
            {getPageNumbers().map((p, i) => (
              <button
                key={i}
                onClick={() => (typeof p === "number" ? setPage(p) : null)}
                disabled={p === "..." || isFetching}
                className={`flex h-[42px] min-w-[42px] items-center justify-center rounded-lg px-2 text-base font-bold font-['Proxima_Nova'] transition-all ${
                  p === page
                    ? "bg-[#1A1F3C] text-white"
                    : p === "..."
                    ? "text-[#A2B2C899] cursor-default"
                    : "text-[#46464D] hover:bg-[#F3F4F5]"
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          <div className="hidden sm:block w-[120px] order-3">
            <OnboardingButton
              title="NEXT"
              variant="outline"
              className="!py-3"
              onClick={() => setPage((p: number) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isFetching}
            />
          </div>
        </div>

        <div className="h-px w-full bg-[#A2B2C833] xl:hidden"></div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-5 w-full xl:w-auto">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const p = parseInt(pageInput);
              if (!isNaN(p) && p >= 1 && p <= totalPages) {
                setPage(p);
              } else {
                setPageInput(String(page));
              }
            }}
            className="flex items-center gap-3"
          >
            <span className="font-['Proxima_Nova'] font-semibold text-[#46464D] whitespace-nowrap">
              Go to:
            </span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onBlur={() => {
                const p = parseInt(pageInput);
                if (!isNaN(p) && p >= 1 && p <= totalPages) {
                  setPage(p);
                } else {
                  setPageInput(String(page));
                }
              }}
              className="w-16 rounded-lg border border-[#A2B2C866] bg-[#F8F9FA] py-2 text-center text-[#030724] font-semibold font-['Proxima_Nova'] outline-none transition focus:border-[#1A1F3C] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </form>

          <div className="hidden h-8 w-px bg-[#A2B2C833] sm:block"></div>

          <div className="flex items-center gap-3 [&_label]:hidden">
            <span className="font-['Proxima_Nova'] font-semibold text-[#46464D] whitespace-nowrap">
              Rows per page
            </span>
            <div className="w-[100px]">
              <OnboardingSelect
                label="Rows per page"
                openUpwards
                value={String(pageSize)}
                onChange={(val: string) => {
                  setPageSize(Number(val));
                  setPage(1);
                }}
                options={[
                  { value: "10", label: "10" },
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                  { value: "100", label: "100" },
                ]}
              />
            </div>
          </div>
        </div>
      </OnboardingCard>
    </div>
  );
};